import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useUserManagement } from '../context/UserManagementContext';

interface UserSearchBarProps {
  onSearch: (query: string, filters: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean }) => void;
  loading?: boolean;
}

export function UserSearchBar({ onSearch, loading = false }: UserSearchBarProps) {
  const { state, setSearchQuery, setSearchFilters, clearSearch } = useUserManagement();
  const [localQuery, setLocalQuery] = useState(state.searchQuery);
  const [showFilters, setShowFilters] = useState(false);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localQuery !== state.searchQuery) {
        setSearchQuery(localQuery);
        onSearch(localQuery, state.searchFilters);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localQuery, state.searchQuery, state.searchFilters, setSearchQuery, onSearch]);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(event.target.value);
  };

  const handleRoleFilterChange = (role: 'PATIENT' | 'CAREGIVER' | 'ADMIN' | '') => {
    const newFilters = { ...state.searchFilters };
    if (role === '') {
      delete newFilters.role;
    } else {
      newFilters.role = role;
    }
    setSearchFilters(newFilters);
    onSearch(localQuery, newFilters);
  };

  const handleStatusFilterChange = (disabled: string) => {
    const newFilters = { ...state.searchFilters };
    if (disabled === '') {
      delete newFilters.disabled;
    } else {
      newFilters.disabled = disabled === 'true';
    }
    setSearchFilters(newFilters);
    onSearch(localQuery, newFilters);
  };

  const handleClearSearch = () => {
    setLocalQuery('');
    clearSearch();
    onSearch('', {});
  };

  const hasActiveFilters = state.searchFilters.role || state.searchFilters.disabled !== undefined;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          fullWidth
          placeholder="Search users by name or email..."
          value={localQuery}
          onChange={handleQueryChange}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (localQuery || hasActiveFilters) && (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Button
          variant={showFilters ? "contained" : "outlined"}
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          size="large"
        >
          Filters
        </Button>
      </Stack>

      {showFilters && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={state.searchFilters.role || ''}
                label="Role"
                onChange={(e) => handleRoleFilterChange(e.target.value as any)}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="PATIENT">Patient</MenuItem>
                <MenuItem value="CAREGIVER">Caregiver</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={state.searchFilters.disabled === undefined ? '' : state.searchFilters.disabled.toString()}
                label="Status"
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* Active filters display */}
      {(localQuery || hasActiveFilters) && (
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {localQuery && (
              <Chip
                label={`Search: "${localQuery}"`}
                onDelete={handleClearSearch}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {state.searchFilters.role && (
              <Chip
                label={`Role: ${state.searchFilters.role}`}
                onDelete={() => handleRoleFilterChange('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
            {state.searchFilters.disabled !== undefined && (
              <Chip
                label={`Status: ${state.searchFilters.disabled ? 'Disabled' : 'Active'}`}
                onDelete={() => handleStatusFilterChange('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
